import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { CheckCircle, Play, ArrowRight, AlertCircle, Users, X, ChevronDown } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { FoundingOfferCountdown } from '../../components/si/FoundingOfferCountdown';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
};

const STORAGE_KEY = 'si_walkthrough_registered';

const TOPICS = [
  'Free account',
  'Pricing',
  'AI setup',
  'Client consent / privacy',
  'My modality / framework',
  'Custom Lens',
  'Something else',
];

// ─── REGISTRATION GATE ───────────────────────────────────────────────────────
function RegistrationGate({ onRegistered }: { onRegistered: (name: string, email: string) => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setLoading(true);
    setError('');
    try {
      const { error: dbError } = await supabase
        .from('si_walkthrough_requests' as never)
        .insert({ name: name.trim(), email: email.trim().toLowerCase() } as never);
      if (dbError && !dbError.message.includes('duplicate')) throw dbError;
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ name: name.trim(), email: email.trim().toLowerCase() }));
      onRegistered(name.trim(), email.trim().toLowerCase());
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f5f8f0] to-white flex flex-col">
      <nav className="bg-white/90 backdrop-blur border-b border-gray-20">
        <div className="max-w-5xl mx-auto px-6 flex items-center justify-between h-16">
          <Link to="/" className="text-sm font-semibold text-gray-80 hover:opacity-80 transition-opacity">
            Session Intelligence™
          </Link>
          <Link to="/si/login" className="text-sm font-medium text-gray-60 hover:text-gray-80 transition-colors">
            Sign In
          </Link>
        </div>
      </nav>

      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 bg-[#e8f0dc] text-[#4a7c2f] text-xs font-semibold px-4 py-1.5 rounded-full">
              <Play className="w-3.5 h-3.5 fill-current" />
              Platform Walkthrough — Watch the Replay
            </div>
          </div>

          <h1 className="text-3xl font-bold text-gray-80 text-center mb-2 leading-tight">
            Watch the Session Intelligence™ Walkthrough
          </h1>
          <p className="text-gray-50 text-center mb-8 text-sm leading-relaxed">
            Enter your name and email to access the walkthrough replay.<br />
            See how Session Intelligence™ works with a real coaching session.
          </p>

          <div className="bg-white rounded-3xl border border-gray-20 shadow-sm p-8">
            {error && (
              <div className="flex items-center gap-2 bg-red-50 text-red-700 p-3.5 rounded-2xl mb-5 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-80 mb-1.5">Your name</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Jane Smith"
                  required
                  disabled={loading}
                  className="w-full px-4 py-3 rounded-2xl border border-gray-20 bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-80 mb-1.5">Email address</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  disabled={loading}
                  className="w-full px-4 py-3 rounded-2xl border border-gray-20 bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors text-sm"
                />
              </div>
              <button
                type="submit"
                disabled={loading || !name.trim() || !email.trim()}
                className="w-full btn-primary justify-center mt-2 inline-flex items-center gap-2"
              >
                {loading ? 'Just a moment…' : 'Watch the Walkthrough'}
                {!loading && <ArrowRight className="w-4 h-4" />}
              </button>
            </form>

            <p className="text-xs text-gray-40 text-center mt-5 leading-relaxed">
              We only use your details to send you your free account link and occasional updates.
            </p>
          </div>
        </motion.div>
      </div>
    </div>
  );
}

// ─── ASK PATRICK MODAL ───────────────────────────────────────────────────────
function AskPatrickModal({ name, email, onClose }: { name: string; email: string; onClose: () => void }) {
  const [message, setMessage] = useState('');
  const [topic, setTopic] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim()) return;
    setSubmitting(true);
    try {
      await supabase
        .from('demo_enquiries' as never)
        .insert({ name, email, message: message.trim(), topic: topic || null, source_page: 'Replay Page' } as never);
      setDone(true);
    } catch {
      // best effort
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        transition={{ duration: 0.2 }}
        className="relative bg-white rounded-3xl shadow-2xl w-full max-w-lg p-8"
      >
        <button
          onClick={onClose}
          className="absolute top-5 right-5 w-8 h-8 rounded-full bg-gray-10 hover:bg-gray-20 flex items-center justify-center transition-colors"
        >
          <X className="w-4 h-4 text-gray-60" />
        </button>

        {done ? (
          <div className="text-center py-6">
            <CheckCircle className="w-14 h-14 text-success-50 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-80 mb-2">Message Received</h2>
            <p className="text-gray-50 text-sm leading-relaxed mb-6">
              Thanks, {name.split(' ')[0]}. Patrick will get back to you at {email}.
            </p>
            <button
              onClick={onClose}
              className="btn-secondary px-8 py-2.5 text-sm font-semibold"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-bold text-gray-80 mb-1">Ask Patrick a Question</h2>
            <p className="text-sm text-gray-50 mb-6 leading-relaxed">
              Not sure if Session Intelligence™ fits your work? Send Patrick a question directly.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-60 mb-1.5">Name</label>
                  <input
                    type="text"
                    value={name}
                    readOnly
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-20 bg-gray-10 text-gray-60 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-60 mb-1.5">Email</label>
                  <input
                    type="email"
                    value={email}
                    readOnly
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-20 bg-gray-10 text-gray-60 text-sm"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-80 mb-1.5">What is your question about? <span className="text-gray-40">(optional)</span></label>
                <div className="relative">
                  <select
                    value={topic}
                    onChange={e => setTopic(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl border border-gray-20 bg-brand-5 text-gray-80 text-sm appearance-none focus:outline-none focus:border-success-40 transition-colors"
                  >
                    <option value="">Select a topic…</option>
                    {TOPICS.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-40 pointer-events-none" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-80 mb-1.5">Your message</label>
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Type your question here…"
                  rows={4}
                  required
                  disabled={submitting}
                  className="w-full px-4 py-3 rounded-2xl border border-gray-20 bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors text-sm resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={submitting || !message.trim()}
                className="w-full btn-primary justify-center inline-flex items-center gap-2"
              >
                {submitting ? 'Sending…' : 'Send My Question'}
                {!submitting && <ArrowRight className="w-4 h-4" />}
              </button>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}

// ─── ACTIVATE ACCOUNT MODAL ──────────────────────────────────────────────────
function ActivateAccountModal({ name, email, onClose }: { name: string; email: string; onClose: () => void }) {
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await supabase
        .from('demo_enquiries' as never)
        .insert({
          name,
          email,
          message: note.trim() || 'Requested account activation from replay page.',
          topic: 'Account Activation',
          source_page: 'Replay Page',
        } as never);
      setDone(true);
    } catch {
      setDone(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 16 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 16 }}
        transition={{ duration: 0.2 }}
        className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md p-8"
      >
        <button
          onClick={onClose}
          className="absolute top-5 right-5 w-8 h-8 rounded-full bg-gray-10 hover:bg-gray-20 flex items-center justify-center transition-colors"
        >
          <X className="w-4 h-4 text-gray-60" />
        </button>

        {done ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-2xl bg-success-10 flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-success-50" />
            </div>
            <h2 className="text-xl font-bold text-gray-80 mb-2">Request Received!</h2>
            <p className="text-gray-50 text-sm leading-relaxed mb-2">
              Patrick will set up your free account and send your login details to:
            </p>
            <p className="font-semibold text-gray-80 text-sm mb-6">{email}</p>
            <p className="text-xs text-gray-40 leading-relaxed mb-6">
              This usually happens within a few hours. Check your inbox (and spam folder just in case).
            </p>
            <button onClick={onClose} className="btn-secondary px-8 py-2.5 text-sm font-semibold">
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 rounded-2xl bg-success-10 flex items-center justify-center mb-5">
              <CheckCircle className="w-6 h-6 text-success-50" />
            </div>
            <h2 className="text-xl font-bold text-gray-80 mb-1">Activate My Free Account</h2>
            <p className="text-sm text-gray-50 mb-6 leading-relaxed">
              Your details are already saved. Hit the button below and Patrick will create your account and send your login link.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="bg-brand-5 rounded-2xl border border-gray-20 p-4 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-50">Name</span>
                  <span className="text-sm font-medium text-gray-80">{name}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-gray-50">Email</span>
                  <span className="text-sm font-medium text-gray-80">{email}</span>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-60 mb-1.5">
                  Anything Patrick should know? <span className="text-gray-40">(optional)</span>
                </label>
                <textarea
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="e.g. I'm an NLP coach working with business clients…"
                  rows={3}
                  disabled={submitting}
                  className="w-full px-4 py-3 rounded-2xl border border-gray-20 bg-brand-5 text-gray-80 placeholder-gray-40 focus:outline-none focus:border-success-40 transition-colors text-sm resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full btn-primary justify-center inline-flex items-center gap-2 py-3"
              >
                {submitting ? 'Sending request…' : 'Yes, Activate My Free Account'}
                {!submitting && <ArrowRight className="w-4 h-4" />}
              </button>

              <p className="text-xs text-gray-40 text-center leading-relaxed">
                No credit card required. One free session analysis included.
              </p>
            </form>
          </>
        )}
      </motion.div>
    </div>
  );
}

// ─── MAIN PAGE ───────────────────────────────────────────────────────────────
export function SIWalkthroughPage() {
  const [registered, setRegistered] = useState<{ name: string; email: string } | null>(null);
  const [checking, setChecking] = useState(true);
  const [showAskModal, setShowAskModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try { setRegistered(JSON.parse(stored)); } catch { /* ignore */ }
    }
    setChecking(false);
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen bg-brand-5 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-success-40 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!registered) {
    return <RegistrationGate onRegistered={(name, email) => setRegistered({ name, email })} />;
  }

  return (
    <AnimatePresence>
      <div className="min-h-screen bg-white font-urbanist text-gray-80">

        {/* NAV */}
        <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur border-b border-gray-20">
          <div className="max-w-5xl mx-auto px-6 flex items-center justify-between h-16">
            <Link to="/" className="text-sm font-semibold text-gray-80 hover:opacity-80 transition-opacity">
              Session Intelligence™
            </Link>
            <div className="flex items-center gap-4">
              <span className="hidden sm:block text-xs text-gray-50">
                Watching as <span className="font-medium text-gray-70">{registered.name}</span>
              </span>
              <Link to="/si/login" className="text-sm font-medium text-gray-60 hover:text-gray-80 transition-colors">
                Sign In
              </Link>
            </div>
          </div>
        </nav>

        {/* FOUNDING OFFER BANNER */}
        <FoundingOfferCountdown />

        {/* HERO */}
        <section className="pt-14 pb-8 bg-gradient-to-b from-[#f5f8f0] to-white">
          <div className="max-w-3xl mx-auto px-6 text-center">
            <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
              <div className="inline-flex items-center gap-2 bg-[#e8f0dc] text-[#4a7c2f] text-xs font-semibold px-4 py-1.5 rounded-full mb-5">
                <Play className="w-3.5 h-3.5 fill-current" />
                Platform Walkthrough — Full Replay
              </div>
            </motion.div>
            <motion.h1
              variants={fadeUp} initial="hidden" animate="visible" custom={1}
              className="text-4xl md:text-5xl font-bold text-gray-80 leading-tight mb-4"
            >
              Watch the Session Intelligence™ Walkthrough
            </motion.h1>
            <motion.p
              variants={fadeUp} initial="hidden" animate="visible" custom={2}
              className="text-lg text-gray-50 leading-relaxed max-w-2xl mx-auto"
            >
              See how Session Intelligence™ helps you turn client sessions into structured insight, better questions, and clearer next-session preparation.
            </motion.p>
          </div>
        </section>

        {/* VIDEO EMBED */}
        <section className="pb-10">
          <div className="max-w-4xl mx-auto px-6">
            <motion.div
              variants={fadeUp} initial="hidden" animate="visible" custom={3}
              className="rounded-3xl overflow-hidden shadow-2xl bg-black"
              style={{ paddingTop: '56.25%', position: 'relative' }}
            >
              <iframe
                src="https://player.vimeo.com/video/1204763045?h=bccf0d86ec&badge=0&autopause=0&player_id=0&app_id=58479"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                referrerPolicy="strict-origin-when-cross-origin"
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                title="Session Intelligence Walkthrough"
              />
            </motion.div>
            <script src="https://player.vimeo.com/api/player.js" />
          </div>
        </section>

        {/* THREE CTAs */}
        <section className="py-10 bg-brand-5">
          <div className="max-w-3xl mx-auto px-6">
            <motion.h2
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
              className="text-2xl font-bold text-gray-80 text-center mb-2"
            >
              Ready to use Session Intelligence™?
            </motion.h2>
            <motion.p
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0.5}
              className="text-gray-50 text-center text-sm mb-8"
            >
              Choose the option that's right for you.
            </motion.p>

            <div className="grid sm:grid-cols-3 gap-4">

              {/* PRIMARY — Activate Free Account */}
              <motion.div
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={0}
                className="bg-gray-80 rounded-3xl p-6 flex flex-col items-center text-center"
              >
                <div className="w-10 h-10 rounded-xl bg-success-50 flex items-center justify-center mb-4">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-white font-bold text-base mb-2">Activate My Free Account</h3>
                <p className="text-gray-40 text-xs leading-relaxed mb-5 flex-1">
                  Start with one free session analysis. No credit card required.
                </p>
                <button
                  onClick={() => setShowActivateModal(true)}
                  className="w-full btn-primary text-sm font-semibold py-2.5 inline-flex items-center justify-center gap-2"
                >
                  Get Started Free
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </motion.div>

              {/* SECONDARY — See Founding Offer */}
              <motion.div
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={1}
                className="bg-white rounded-3xl border border-gray-20 p-6 flex flex-col items-center text-center"
              >
                <div className="w-10 h-10 rounded-xl bg-[#e8f0dc] flex items-center justify-center mb-4">
                  <Play className="w-5 h-5 text-[#4a7c2f] fill-current" />
                </div>
                <h3 className="text-gray-80 font-bold text-base mb-2">See the Founding Offer</h3>
                <p className="text-gray-50 text-xs leading-relaxed mb-5 flex-1">
                  View early-access pricing and full access options.
                </p>
                <Link
                  to="/si/pricing"
                  className="w-full btn-secondary text-sm font-semibold py-2.5 text-center inline-flex items-center justify-center gap-2"
                >
                  View Pricing
                  <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </motion.div>

              {/* TERTIARY — Ask Patrick */}
              <motion.div
                variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={2}
                className="bg-white rounded-3xl border border-gray-20 p-6 flex flex-col items-center text-center"
              >
                <div className="w-10 h-10 rounded-xl bg-gray-10 flex items-center justify-center mb-4">
                  <Users className="w-5 h-5 text-gray-60" />
                </div>
                <h3 className="text-gray-80 font-bold text-base mb-2">Ask Patrick a Question</h3>
                <p className="text-gray-50 text-xs leading-relaxed mb-5 flex-1">
                  Not sure if this fits your work? Send a question directly.
                </p>
                <button
                  onClick={() => setShowAskModal(true)}
                  className="w-full btn-secondary text-sm font-semibold py-2.5 inline-flex items-center justify-center gap-2"
                >
                  Ask a Question
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              </motion.div>

            </div>
          </div>
        </section>

        {/* WHAT YOU SAW */}
        <section className="py-14 bg-white">
          <div className="max-w-3xl mx-auto px-6">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-80 mb-3">What the Walkthrough Covers</h2>
              <p className="text-gray-50">A live demonstration from a real coaching session through to a complete insight report.</p>
            </motion.div>

            <div className="space-y-4">
              {[
                { n: '01', title: 'Upload a Real Session', body: 'Watch an audio file get transcribed live — no staging, no pre-loaded results.' },
                { n: '02', title: 'Review the Insight Report', body: 'See every section: limiting beliefs, NLP meta model patterns, emotional themes, parts conflicts, breakthrough zone, and suggested coach questions.' },
                { n: '03', title: 'Explore the AI Discussion Tab', body: 'Ask follow-up questions and explore specific patterns with AI that has full session context.' },
                { n: '04', title: 'Full Dashboard Walkthrough', body: 'Client profiles, session history, pattern tracking, next-session planner, and Custom Lens™.' },
              ].map((step, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i * 0.08}
                  className="bg-brand-5 rounded-2xl border border-gray-20 p-6 flex gap-5"
                >
                  <div className="w-10 h-10 rounded-xl bg-gray-80 text-white text-sm font-bold flex items-center justify-center flex-shrink-0">
                    {step.n}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-80 mb-1">{step.title}</h3>
                    <p className="text-gray-50 text-sm leading-relaxed">{step.body}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* WHO IS THIS FOR */}
        <section className="py-14 bg-brand-5">
          <div className="max-w-3xl mx-auto px-6">
            <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-80 mb-3">Who Session Intelligence™ is For</h2>
            </motion.div>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                'Coaches who want to use AI responsibly in their practice',
                'Coaches who want to understand clients more deeply',
                'Coaches who want to prepare better before each session',
                'NLP practitioners curious about session analysis',
                'Counsellors and hypnotherapists exploring AI tools',
                'Leadership and business coaches wanting pattern clarity',
              ].map((item, i) => (
                <motion.div
                  key={i}
                  variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i * 0.05}
                  className="flex items-center gap-3 bg-white rounded-2xl border border-gray-20 px-5 py-3.5"
                >
                  <CheckCircle className="w-4 h-4 text-[#4a7c2f] flex-shrink-0" />
                  <span className="text-gray-70 text-sm">{item}</span>
                </motion.div>
              ))}
            </div>

            {/* Bottom CTA repeat */}
            <motion.div
              variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}
              className="mt-10 text-center"
            >
              <button
                onClick={() => setShowActivateModal(true)}
                className="btn-primary px-8 py-3 inline-flex items-center gap-2 text-sm font-semibold"
              >
                Activate My Free Account
                <ArrowRight className="w-4 h-4" />
              </button>
              <p className="text-xs text-gray-40 mt-3">One free session analysis. No credit card required.</p>
            </motion.div>
          </div>
        </section>

        {/* FOOTER */}
        <footer className="py-10 bg-gray-80 border-t border-white/10">
          <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <span className="text-gray-40 text-sm">Session Intelligence™</span>
            <div className="flex items-center gap-6 text-gray-40 text-sm">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            </div>
            <p className="text-gray-50 text-xs">Copyright 2025 Session Intelligence™, All Rights Reserved.</p>
          </div>
        </footer>

      </div>

      {/* ASK PATRICK MODAL */}
      <AnimatePresence>
        {showAskModal && (
          <AskPatrickModal
            name={registered.name}
            email={registered.email}
            onClose={() => setShowAskModal(false)}
          />
        )}
      </AnimatePresence>

      {/* ACTIVATE ACCOUNT MODAL */}
      <AnimatePresence>
        {showActivateModal && (
          <ActivateAccountModal
            name={registered.name}
            email={registered.email}
            onClose={() => setShowActivateModal(false)}
          />
        )}
      </AnimatePresence>
    </AnimatePresence>
  );
}
